import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "../ui/calendar";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Wrench, Calendar as CalendarIcon, Clock, ChevronDown, Check, X } from "lucide-react";
import { sanitizePhoneInput, phoneError } from "../../utils/phone";
import { formatPlateInput, plateError } from "../../utils/plate";
import { Checkbox } from "../ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BookingSection = ({ services, selectedService, onSelectService, content }) => {
  const cfg = content?.booking || {
    badge: "Réservation",
    title: "Prendre Rendez-vous",
    subtitle: "",
    cta_confirm: "Confirmer le rendez-vous",
  };
  const [date, setDate] = useState(undefined);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [slotsMessage, setSlotsMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    plate: "",
    tireSize: "",
    tireType: "",
    optimTypes: [], // Changed to array for multi-select
    entretienType: "",
    brakeOptions: [],
    otherRequest: "", // For "Autre demande" service
  });
  const phoneInvalid = phoneError(formData.phone);
  const [plateForeign, setPlateForeign] = useState(false);
  const plateInvalid = plateError(formData.plate, plateForeign);

  // Check if selected service is specific type
  const isPneumatiques = selectedService?.name === "Pneumatiques";
  const isOptimisation = selectedService?.name === "Mise à jour calculateur";
  const isEntretien = selectedService?.name === "Entretien Complet";
  const isFreinage = selectedService?.name === "Freinage & Contrôles";
  const isOtherRequest = selectedService?.requires_date === false || selectedService?.id === "service-other";

  // Vacation/pause helper: "Reprise le 5 octobre 2026" formatting for banners and select items.
  const formatResumeDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  };
  const isServicePaused = (svc) => {
    if (!svc?.unavailable_until) return false;
    const today = new Date().toISOString().slice(0, 10);
    return today < svc.unavailable_until;
  };
  const selectedServicePausedUntil = isServicePaused(selectedService)
    ? selectedService.unavailable_until
    : null;

  // Optimisation options for multi-select
  const optimOptionsList = [
    { id: "gain-puissance", label: "Gain de puissance" },
    { id: "conversion-flexfuel", label: "Conversion flexfuel" },
    { id: "service-adblue", label: "Service Adblue" },
    { id: "service-egr", label: "Service EGR" },
    { id: "service-dpf", label: "Service DPF" },
  ];

  const toggleOptimOption = (optionId) => {
    setFormData((prev) => {
      const current = prev.optimTypes || [];
      if (current.includes(optionId)) {
        return { ...prev, optimTypes: current.filter((id) => id !== optionId) };
      } else {
        return { ...prev, optimTypes: [...current, optionId] };
      }
    });
  };

  // Brake options for multi-select
  const brakeOptionsList = [
    { id: "plaquettes-avant", label: "Plaquettes de frein avant" },
    { id: "disques-plaquettes-avant", label: "Disques et plaquettes de frein à l'avant" },
    { id: "plaquettes-arriere", label: "Plaquettes de frein à l'arrière" },
    { id: "disques-plaquettes-arriere", label: "Disques et plaquettes de frein à l'arrière" },
  ];

  const toggleBrakeOption = (optionId) => {
    setFormData((prev) => {
      const current = prev.brakeOptions || [];
      if (current.includes(optionId)) {
        return { ...prev, brakeOptions: current.filter((id) => id !== optionId) };
      } else {
        return { ...prev, brakeOptions: [...current, optionId] };
      }
    });
  };

  // Fetch available slots when date or service changes (only if requires_date)
  useEffect(() => {
    const fetchSlots = async () => {
      if (!date || !selectedService || isOtherRequest) return;

      // Format date as YYYY-MM-DD in local timezone (not UTC)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      console.log('Fetching slots for:', dateStr, 'service:', selectedService.id);
      
      try {
        const response = await axios.get(
          `${API}/appointments/available-slots?date=${dateStr}&service_id=${selectedService.id}`
        );
        console.log('Slots received:', response.data);
        setAvailableSlots(response.data.available_slots || []);
        setSlotsMessage(response.data.message || "");
        setSelectedSlot("");
      } catch (error) {
        console.error("Error fetching slots:", error);
        setAvailableSlots([]);
        setSlotsMessage("");
      }
    };

    fetchSlots();
  }, [date, selectedService, isOtherRequest]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // For "Autre demande", don't require date/slot
    if (isOtherRequest) {
      if (!formData.name || !formData.email || !formData.phone) {
        toast.error("Veuillez remplir tous les champs obligatoires");
        return;
      }
      if (!formData.otherRequest) {
        toast.error("Veuillez décrire votre demande");
        return;
      }
    } else {
      if (!selectedService || !date || !selectedSlot) {
        toast.error("Veuillez sélectionner un service, une date et un créneau");
        return;
      }
      if (!formData.name || !formData.email || !formData.phone || !formData.plate) {
        toast.error("Veuillez remplir tous les champs");
        return;
      }
    }

    if (phoneInvalid) {
      toast.error(`Téléphone invalide : ${phoneInvalid}`);
      return;
    }
    if (plateInvalid) {
      toast.error(`Plaque invalide : ${plateInvalid}`);
      return;
    }

    // Validation spécifique pour Pneumatiques
    if (isPneumatiques && (!formData.tireSize || !formData.tireType)) {
      toast.error("Veuillez indiquer la dimension et le type de pneu");
      return;
    }

    // Validation spécifique pour Optimisation Moteur
    if (isOptimisation && (!formData.optimTypes || formData.optimTypes.length === 0)) {
      toast.error("Veuillez sélectionner au moins une option de service");
      return;
    }

    // Validation spécifique pour Entretien Complet
    if (isEntretien && !formData.entretienType) {
      toast.error("Veuillez choisir le type d'entretien");
      return;
    }

    // Validation spécifique pour Freinage
    if (isFreinage && (!formData.brakeOptions || formData.brakeOptions.length === 0)) {
      toast.error("Veuillez sélectionner au moins une option de freinage");
      return;
    }

    setLoading(true);

    try {
      // For "Autre demande", don't send date/time
      let dateStr = "";
      let timeSlot = "";
      
      if (!isOtherRequest && date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        dateStr = `${year}-${month}-${day}`;
        timeSlot = selectedSlot;
      }
      
      // Build notes with request details
      let notes = "";
      if (isOtherRequest && formData.otherRequest) {
        notes = formData.otherRequest;
      }
      
      // Build optimTypes string from selected options
      const optimTypesStr = isOptimisation 
        ? formData.optimTypes.map(id => optimOptionsList.find(o => o.id === id)?.label).filter(Boolean).join(", ")
        : "";
      
      const response = await axios.post(`${API}/appointments`, {
        service_id: selectedService.id,
        service_name: selectedService.name,
        client_name: formData.name,
        client_email: formData.email,
        client_phone: formData.phone,
        client_plate: formData.plate || "",
        client_plate_foreign: plateForeign,
        tire_size: isPneumatiques ? formData.tireSize : "",
        tire_type: isPneumatiques ? formData.tireType : "",
        optim_type: optimTypesStr,
        entretien_type: isEntretien ? formData.entretienType : "",
        brake_options: isFreinage ? formData.brakeOptions.join(", ") : "",
        notes: notes,
        date: dateStr,
        time: timeSlot,
      });

      if (isOtherRequest) {
        toast.success("Demande envoyée ! Nous vous recontacterons rapidement pour fixer un rendez-vous.");
      } else {
        toast.success("Rendez-vous confirmé ! Vous recevrez un rappel.");
      }

      // Reset form
      setFormData({ name: "", email: "", phone: "", plate: "", tireSize: "", tireType: "", optimTypes: [], entretienType: "", brakeOptions: [], otherRequest: "" });
      setDate(undefined);
      setSelectedSlot("");
      onSelectService(null);
    } catch (error) {
      const message =
        error.response?.data?.detail || "Erreur lors de la réservation";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate minimum booking date
  // For Pneumatiques: 1 week delay for tire ordering
  const getMinimumDate = () => {
    const today = new Date();
    if (isPneumatiques) {
      const oneWeekLater = new Date(today);
      oneWeekLater.setDate(today.getDate() + 7);
      return oneWeekLater;
    }
    return today;
  };

  // Disable past dates, Saturdays and Sundays
  // For Pneumatiques: also disable the next 7 days
  // For paused services (vacation): also disable dates before the resume date
  const disabledDays = [
    { before: getMinimumDate() },
    { dayOfWeek: [0, 6] }, // Sunday and Saturday
    ...(selectedServicePausedUntil
      ? [{ before: new Date(selectedServicePausedUntil + "T00:00:00") }]
      : []),
  ];

  // French locale for calendar
  const frenchLocale = {
    localize: {
      day: (n) => ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'][n],
      month: (n) => ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'][n],
    },
    formatLong: {
      date: () => 'dd/MM/yyyy',
    },
  };

  return (
    <section id="booking" className="py-24 md:py-32 px-4 bg-[#0F0F0F]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#FF3B30] mb-4">
            {cfg.badge}
          </p>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase text-white">
            {cfg.title}
          </h2>
          {cfg.subtitle && (
            <p className="text-white/60 text-base md:text-lg mt-4 max-w-2xl mx-auto">
              {cfg.subtitle}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Service Selection & Calendar */}
          <div className="space-y-8">
            {/* Service Selector */}
            <div className="bg-[#121212] border border-white/10 p-6 rounded-lg">
              <Label className="text-white/60 text-sm font-bold tracking-wider uppercase mb-4 block">
                1. Choisir un service
              </Label>
              <Select
                value={selectedService?.id || ""}
                onValueChange={(value) => {
                  const service = services.find((s) => s.id === value);
                  onSelectService(service);
                }}
              >
                <SelectTrigger
                  data-testid="service-select"
                  className="bg-[#1C1C1E] border-white/10 text-white h-12"
                >
                  <SelectValue placeholder="Sélectionner un service" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1E] border-white/10">
                  {services.map((service) => {
                    const paused = isServicePaused(service);
                    return (
                      <SelectItem
                        key={service.id}
                        value={service.id}
                        data-testid={`service-option-${service.id}`}
                        className="text-white hover:bg-[#FF3B30]/20"
                      >
                        <span className="flex items-center gap-2 flex-wrap">
                          <span>{service.name}</span>
                          {paused && (
                            <span className="text-[10px] uppercase tracking-wider bg-[#FF3B30]/20 text-[#FF3B30] px-1.5 py-0.5 rounded">
                              Reprise le {formatResumeDate(service.unavailable_until)}
                            </span>
                          )}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {selectedServicePausedUntil && (
                <div
                  data-testid="service-paused-banner"
                  className="mt-3 flex items-start gap-3 p-4 rounded-md bg-[#FF3B30]/[0.06] border border-[#FF3B30]/25"
                >
                  <div className="w-8 h-8 rounded-full bg-[#FF3B30]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CalendarIcon size={14} className="text-[#FF3B30]" />
                  </div>
                  <div className="text-sm text-white/80 leading-relaxed">
                    Ce service est mis en pause pendant nos congés — reprise à partir du{" "}
                    <span className="text-white font-semibold">
                      {formatResumeDate(selectedServicePausedUntil)}
                    </span>
                    .{" "}
                    {isOtherRequest
                      ? "Vous pouvez déposer votre demande dès aujourd'hui, nous vous répondrons à notre retour."
                      : "Vous pouvez déjà réserver un créneau à partir de cette date."}
                  </div>
                </div>
              )}
            </div>

            {/* Calendar - Hidden only for "Autre demande" (no date needed) */}
            {!isOtherRequest && (
              <div className="bg-[#121212] border border-white/10 p-6 rounded-lg">
                <Label className="text-white/60 text-sm font-bold tracking-wider uppercase mb-4 block">
                  2. Choisir une date
                </Label>
                {isPneumatiques && (
                  <p className="text-[#FF3B30] text-sm mb-4 bg-[#FF3B30]/10 p-3 rounded border border-[#FF3B30]/30">
                    ⚠️ Délai de 7 jours requis pour la commande des pneus
                  </p>
                )}
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={disabledDays}
                    locale={frenchLocale}
                    weekStartsOn={1}
                    className="bg-transparent text-white"
                    data-testid="booking-calendar"
                  />
                </div>
              </div>
            )}

            {/* Time Slots - Hidden only for "Autre demande" (no date needed) */}
            {!isOtherRequest && date && selectedService && (
              <div className="bg-[#121212] border border-white/10 p-6 rounded-lg">
                <Label className="text-white/60 text-sm font-bold tracking-wider uppercase mb-4 block">
                  3. Choisir un créneau
                </Label>
                {availableSlots.length > 0 ? (
                  <div className="grid grid-cols-4 gap-2">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot}
                        type="button"
                        data-testid={`time-slot-${slot}`}
                        onClick={() => setSelectedSlot(slot)}
                        className={`py-3 px-2 text-sm font-medium transition-all duration-200 border
                          ${
                            selectedSlot === slot
                              ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                              : "bg-[#1C1C1E] border-white/10 text-white/70 hover:border-[#FF3B30]/50"
                          }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/40 text-center py-4">
                    Aucun créneau disponible pour cette date
                  </p>
                )}
              </div>
            )}
            
            {/* "Autre demande" description box */}
            {isOtherRequest && (
              <div className="bg-[#121212] border border-[#FF3B30]/30 p-6 rounded-lg">
                <Label className="text-white/60 text-sm font-bold tracking-wider uppercase mb-4 block">
                  2. Décrivez votre demande
                </Label>
                <p className="text-white/60 text-sm mb-4">
                  Expliquez-nous votre besoin et nous vous recontacterons pour fixer un rendez-vous adapté.
                </p>
                <textarea
                  value={formData.otherRequest}
                  onChange={(e) => setFormData({ ...formData, otherRequest: e.target.value })}
                  className="w-full bg-[#1C1C1E] border border-white/10 text-white rounded-lg px-4 py-3 min-h-[120px] focus:border-[#FF3B30] focus:outline-none"
                  placeholder="Ex: Je souhaite faire un diagnostic de mon véhicule, changer mon embrayage, installer un kit turbo..."
                  required
                />
              </div>
            )}
          </div>

          {/* Right: Contact Form */}
          <div className="bg-[#121212] border border-white/10 p-8 rounded-lg h-fit">
            <Label className="text-white/60 text-sm font-bold tracking-wider uppercase mb-6 block">
              {isOtherRequest ? "3. Vos coordonnées" : "4. Vos coordonnées"}
            </Label>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label
                  htmlFor="name"
                  className="text-white/80 text-sm mb-2 block"
                >
                  Nom complet
                </Label>
                <Input
                  id="name"
                  data-testid="input-name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Jean Dupont"
                  className="bg-[#1C1C1E] border-white/10 text-white h-12 focus:border-[#FF3B30]"
                />
              </div>

              <div>
                <Label
                  htmlFor="email"
                  className="text-white/80 text-sm mb-2 block"
                >
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="input-email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="jean@exemple.fr"
                  className="bg-[#1C1C1E] border-white/10 text-white h-12 focus:border-[#FF3B30]"
                />
              </div>

              <div>
                <Label
                  htmlFor="phone"
                  className="text-white/80 text-sm mb-2 block"
                >
                  Téléphone
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  data-testid="input-phone"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: sanitizePhoneInput(e.target.value) })
                  }
                  placeholder="06 12 34 56 78"
                  aria-invalid={phoneInvalid ? "true" : "false"}
                  className={`bg-[#1C1C1E] text-white h-12 focus:border-[#FF3B30] ${
                    phoneInvalid ? "border-red-500/70" : "border-white/10"
                  }`}
                />
                {phoneInvalid && (
                  <p data-testid="input-phone-error" className="text-red-400 text-xs mt-1.5">
                    {phoneInvalid}
                  </p>
                )}
              </div>

              <div>
                <Label
                  htmlFor="plate"
                  className="text-white/80 text-sm mb-2 block"
                >
                  Plaque d&apos;immatriculation
                </Label>
                <Input
                  id="plate"
                  data-testid="input-plate"
                  value={formData.plate}
                  onChange={(e) =>
                    setFormData({ ...formData, plate: formatPlateInput(e.target.value, plateForeign) })
                  }
                  placeholder={plateForeign ? "Plaque étrangère" : "AB-123-CD"}
                  autoCapitalize="characters"
                  aria-invalid={plateInvalid ? "true" : "false"}
                  className={`bg-[#1C1C1E] text-white h-12 focus:border-[#FF3B30] uppercase font-mono ${
                    plateInvalid ? "border-red-500/70" : "border-white/10"
                  }`}
                />
                {plateInvalid && (
                  <p data-testid="input-plate-error" className="text-red-400 text-xs mt-1.5">
                    {plateInvalid}
                  </p>
                )}
                <label className="flex items-center gap-2 mt-2 text-white/60 text-xs cursor-pointer select-none">
                  <Checkbox
                    checked={plateForeign}
                    onCheckedChange={(v) => {
                      setPlateForeign(!!v);
                      setFormData({ ...formData, plate: formatPlateInput(formData.plate, !!v) });
                    }}
                    data-testid="input-plate-foreign"
                    className="border-white/30 data-[state=checked]:bg-[#FF3B30] data-[state=checked]:border-[#FF3B30]"
                  />
                  Plaque étrangère
                </label>
              </div>

              {/* Champs spécifiques Pneumatiques */}
              {isPneumatiques && (
                <>
                  <div>
                    <Label
                      htmlFor="tireSize"
                      className="text-white/80 text-sm mb-2 block"
                    >
                      Dimension du pneu
                    </Label>
                    <Input
                      id="tireSize"
                      data-testid="input-tire-size"
                      value={formData.tireSize}
                      onChange={(e) =>
                        setFormData({ ...formData, tireSize: e.target.value.toUpperCase() })
                      }
                      placeholder="205/55 R16"
                      className="bg-[#1C1C1E] border-white/10 text-white h-12 focus:border-[#FF3B30] uppercase"
                    />
                  </div>

                  <div>
                    <Label
                      htmlFor="tireType"
                      className="text-white/80 text-sm mb-2 block"
                    >
                      Type de pneu
                    </Label>
                    <Select
                      value={formData.tireType}
                      onValueChange={(value) =>
                        setFormData({ ...formData, tireType: value })
                      }
                    >
                      <SelectTrigger
                        data-testid="tire-type-select"
                        className="bg-[#1C1C1E] border-white/10 text-white h-12"
                      >
                        <SelectValue placeholder="Choisir le type de pneu" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1C1C1E] border-white/10">
                        <SelectItem value="Été" className="text-white hover:bg-[#FF3B30]/20">
                          Été
                        </SelectItem>
                        <SelectItem value="Quatre Saisons" className="text-white hover:bg-[#FF3B30]/20">
                          Quatre Saisons
                        </SelectItem>
                        <SelectItem value="Hiver" className="text-white hover:bg-[#FF3B30]/20">
                          Hiver
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {/* Champs spécifiques Optimisation Moteur - Multi-select */}
              {isOptimisation && (
                <div>
                  <Label className="text-white/80 text-sm mb-2 block">
                    Type de service <span className="text-white/50">(sélection multiple possible)</span>
                  </Label>
                  <div className="space-y-2">
                    {optimOptionsList.map((option) => (
                      <label
                        key={option.id}
                        data-testid={`optim-option-${option.id}`}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                          formData.optimTypes?.includes(option.id)
                            ? "bg-[#FF3B30]/20 border border-[#FF3B30]"
                            : "bg-[#1C1C1E] border border-white/10 hover:border-white/30"
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          formData.optimTypes?.includes(option.id)
                            ? "bg-[#FF3B30] border-[#FF3B30]"
                            : "border-white/30"
                        }`}>
                          {formData.optimTypes?.includes(option.id) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={formData.optimTypes?.includes(option.id)}
                          onChange={() => toggleOptimOption(option.id)}
                        />
                        <span className="text-white/80 text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Champs spécifiques Entretien Complet */}
              {isEntretien && (
                <div>
                  <Label
                    htmlFor="entretienType"
                    className="text-white/80 text-sm mb-2 block"
                  >
                    Type d&apos;entretien
                  </Label>
                  <Select
                    value={formData.entretienType}
                    onValueChange={(value) =>
                      setFormData({ ...formData, entretienType: value })
                    }
                  >
                    <SelectTrigger
                      data-testid="entretien-type-select"
                      className="bg-[#1C1C1E] border-white/10 text-white h-12"
                    >
                      <SelectValue placeholder="Choisir le type d'entretien" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C1C1E] border-white/10">
                      <SelectItem value="Vidange moteur + filtre à huile" className="text-white hover:bg-[#FF3B30]/20">
                        Vidange moteur + filtre à huile
                      </SelectItem>
                      <SelectItem value="Vidange moteur + tous les filtres" className="text-white hover:bg-[#FF3B30]/20">
                        Vidange moteur + tous les filtres
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Champs spécifiques Freinage - Multi-select avec checkboxes */}
              {isFreinage && (
                <div>
                  <Label className="text-white/80 text-sm mb-3 block">
                    Options de freinage <span className="text-white/50">(sélection multiple possible)</span>
                  </Label>
                  <div className="space-y-2">
                    {brakeOptionsList.map((option) => (
                      <label
                        key={option.id}
                        data-testid={`brake-option-${option.id}`}
                        className={`flex items-center gap-3 p-3 rounded border cursor-pointer transition-all duration-200 ${
                          formData.brakeOptions?.includes(option.id)
                            ? "bg-[#FF3B30]/10 border-[#FF3B30]"
                            : "bg-[#1C1C1E] border-white/10 hover:border-white/30"
                        }`}
                      >
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                            formData.brakeOptions?.includes(option.id)
                              ? "bg-[#FF3B30] border-[#FF3B30]"
                              : "border-white/30"
                          }`}
                        >
                          {formData.brakeOptions?.includes(option.id) && (
                            <Check size={14} className="text-white" />
                          )}
                        </div>
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={formData.brakeOptions?.includes(option.id)}
                          onChange={() => toggleBrakeOption(option.id)}
                        />
                        <span className="text-white/80 text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary - for standard services with date */}
              {selectedService && !isOtherRequest && date && selectedSlot && (
                <div className="bg-[#1C1C1E] p-4 border border-white/10 rounded">
                  <p className="text-sm text-white/60 mb-2">Récapitulatif :</p>
                  <p className="text-white font-medium">
                    {selectedService.name}
                  </p>
                  <p className="text-white/70 text-sm">
                    {date.toLocaleDateString("fr-FR", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}{" "}
                    à {selectedSlot}
                  </p>
                  {isPneumatiques && formData.tireSize && formData.tireType && (
                    <p className="text-white/70 text-sm mt-1">
                      Pneus: {formData.tireSize} - {formData.tireType}
                    </p>
                  )}
                  {isOptimisation && formData.optimTypes?.length > 0 && (
                    <div className="text-white/70 text-sm mt-1">
                      <p>Services sélectionnés:</p>
                      <ul className="list-disc list-inside ml-2">
                        {formData.optimTypes.map((optId) => {
                          const opt = optimOptionsList.find((o) => o.id === optId);
                          return opt ? <li key={optId}>{opt.label}</li> : null;
                        })}
                      </ul>
                    </div>
                  )}
                  {isEntretien && formData.entretienType && (
                    <p className="text-white/70 text-sm mt-1">
                      Type: {formData.entretienType}
                    </p>
                  )}
                  {isFreinage && formData.brakeOptions?.length > 0 && (
                    <div className="text-white/70 text-sm mt-1">
                      <p>Options sélectionnées:</p>
                      <ul className="list-disc list-inside ml-2">
                        {formData.brakeOptions.map((optId) => {
                          const opt = brakeOptionsList.find((o) => o.id === optId);
                          return opt ? <li key={optId}>{opt.label}</li> : null;
                        })}
                      </ul>
                    </div>
                  )}
                  <p className="text-[#FF3B30] font-bold mt-2">
                    Prix sur devis
                  </p>
                </div>
              )}
              
              {/* Summary - for "Autre demande" */}
              {isOtherRequest && formData.otherRequest && (
                <div className="bg-[#1C1C1E] p-4 border border-[#FF3B30]/30 rounded">
                  <p className="text-sm text-white/60 mb-2">Récapitulatif :</p>
                  <p className="text-white font-medium">
                    {selectedService.name}
                  </p>
                  <p className="text-white/70 text-sm mt-2">
                    {formData.otherRequest.substring(0, 100)}{formData.otherRequest.length > 100 ? '...' : ''}
                  </p>
                  <p className="text-[#FF3B30] text-sm mt-3">
                    📞 Nous vous recontacterons pour fixer un rendez-vous
                  </p>
                </div>
              )}

              <button
                type="submit"
                data-testid="submit-booking-btn"
                disabled={loading || !!phoneInvalid || !!plateInvalid || !selectedService || (!isOtherRequest && (!date || !selectedSlot)) || (isOtherRequest && !formData.otherRequest)}
                className="w-full bg-[#FF3B30] hover:bg-[#D63025] disabled:bg-white/10 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wider py-4 transform skew-x-[-10deg] transition-all duration-300 hover:scale-[1.02] shadow-[0_0_20px_rgba(255,59,48,0.3)]"
              >
                <span className="skew-content inline-block">
                  {loading ? "Envoi..." : (isOtherRequest ? "Envoyer ma demande" : cfg.cta_confirm)}
                </span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============== MARQUEE SECTION ==============
export const MarqueeSection = () => {
  const text = "AUVERGNE TECH RACING • PERFORMANCE • PRÉCISION • RAPIDITÉ • ";

  return (
    <div className="bg-[#FF3B30] py-4 overflow-hidden">
      <div className="marquee whitespace-nowrap">
        <span className="text-white font-black text-2xl tracking-[0.1em] uppercase italic">
          {text.repeat(10)}
        </span>
      </div>
    </div>
  );
};

// ============== GOOGLE REVIEWS SECTION ==============
